import React, { useState, useEffect } from 'react';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import OrderDetailsModal from './OrderDetailsModal';
import JakeOrderModal from './JakeOrderModal';
import InquiriesTab from './InquiriesTab';
import './Dashboard.css';

const Dashboard = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState('all-time');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const itemsPerPage = 10;

  // Helper function to get date range based on selection
  const getDateRange = (rangeType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let startDate, endDate;

    switch (rangeType) {
      case 'today':
        startDate = new Date(today);
        endDate = new Date(tomorrow);
        break;
      case 'last-week':
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        endDate = new Date(tomorrow);
        break;
      case 'this-month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        break;
      case 'last-month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default:
        return null;
    }

    return { startDate, endDate };
  };

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      console.log('Fetching orders from:', api.defaults.baseURL + '/special-orders/orders');
      const response = await api.get('/special-orders/orders', {
        params: {
          limit: 1000, // Fetch up to 1000 orders to display all at once
        },
      });
      console.log('Orders response:', response.data);
      const ordersList = response.data.orders || [];
      console.log('Number of orders:', ordersList.length);
      setOrders(ordersList);
      setError(null);
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Failed to fetch orders';
      console.error('Fetch orders error:', err);
      console.error('Error details:', {
        status: err.response?.status,
        message: errorMsg,
        url: err.config?.url,
      });
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Filter orders based on status, search, and date range
  const filteredOrders = orders.filter((order) => {
    const matchesStatus = filterStatus === 'all' || order.payment_status === filterStatus;

    // Check date range filter
    let matchesDate = true;
    if (dateRange !== 'all-time') {
      const orderDate = new Date(order.created_at);
      orderDate.setHours(0, 0, 0, 0);

      if (dateRange === 'custom') {
        // Custom date range
        if (customStartDate && customEndDate) {
          const startDate = new Date(customStartDate);
          const endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
          matchesDate = orderDate >= startDate && orderDate <= endDate;
        }
      } else {
        // Predefined date ranges
        const range = getDateRange(dateRange);
        if (range) {
          matchesDate = orderDate >= range.startDate && orderDate < range.endDate;
        }
      }
    }

    // If no search term, check status and date
    if (!searchTerm.trim()) {
      return matchesStatus && matchesDate;
    }

    // Search across multiple fields
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      (order.po_number && order.po_number.toLowerCase().includes(searchLower)) ||
      (order.customer_name && order.customer_name.toLowerCase().includes(searchLower)) ||
      (order.item_name && order.item_name.toLowerCase().includes(searchLower)) ||
      (order.item_color && order.item_color.toLowerCase().includes(searchLower)) ||
      (order.item_sku && order.item_sku.toLowerCase().includes(searchLower));

    return matchesStatus && matchesSearch && matchesDate;
  });

  // Paginate orders
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  // Debug log
  if (orders.length > 0 && filteredOrders.length === 0) {
    console.warn('WARNING: Orders exist but none match current filters', {
      totalOrders: orders.length,
      filterStatus,
      searchTerm,
      firstOrder: orders[0],
    });
  }

  const handleCreateOrder = () => {
    setIsCreateOrderModalOpen(true);
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderSuccess = () => {
    // Refresh orders list after edit/delete
    fetchOrders();
  };

  const handleToggleReceived = async (orderId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await api.patch(`/special-orders/orders/${orderId}/received`, {
        customer_received: newStatus,
      });
      // Update local state
      setOrders(orders.map(order =>
        order.id === orderId
          ? { ...order, customer_received: newStatus }
          : order
      ));
    } catch (err) {
      console.error('Failed to update received status:', err);
      alert('Failed to update received status');
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    // Format as (XXX)-XXX-XXXX
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // Return original if not 10 digits
    return phone;
  };


  return (
    <div className="special-orders-dashboard">
      <div className="dashboard-header">
        <p>Manage and track custom orders</p>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Orders
        </button>
        <button
          className={`tab-button ${activeTab === 'inquiries' ? 'active' : ''}`}
          onClick={() => setActiveTab('inquiries')}
        >
          Inquiries
        </button>
      </div>

      {activeTab === 'inquiries' ? (
        <InquiriesTab />
      ) : (
        <>
      {/* Stats Section */}
      <div className="stats-grid">
        <div className="stat-card">
          <h4>Total Orders</h4>
          <p className="stat-value">{filteredOrders.length}</p>
        </div>
        <div className="stat-card">
          <h4>Total Sales</h4>
          <p className="stat-value" style={{ color: '#667eea' }}>
            ${filteredOrders.reduce((sum, o) => sum + (parseFloat(o.price || 0) * parseInt(o.quantity || 1)), 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="stat-card">
          <h4>Not Paid</h4>
          <p className="stat-value" style={{ color: '#FFA500' }}>
            {filteredOrders.filter((o) => o.payment_status === 'Not Paid').length}
          </p>
        </div>
        <div className="stat-card">
          <h4>Paid in Full</h4>
          <p className="stat-value" style={{ color: '#228B22' }}>
            {filteredOrders.filter((o) => o.payment_status === 'Paid in Full').length}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        {/* Top Row - Search and Action Button */}
        <div className="controls-top-row">
          <div className="search-filter">
            <input
              type="text"
              placeholder="Search by PO number, customer, or product..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="search-input"
            />

            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="Not Paid">Not Paid</option>
              <option value="Paid in Full">Paid in Full</option>
            </select>
          </div>

          <button onClick={handleCreateOrder} className="btn-primary">
            + Create Order
          </button>
        </div>

        {/* Bottom Row - Date Range Filter */}
        <div className="date-filter-container">
          <span className="filter-label">Date Range:</span>
          <div className="date-filter">
            <button
              className={`date-btn ${dateRange === 'all-time' ? 'active' : ''}`}
              onClick={() => {
                setDateRange('all-time');
                setCurrentPage(1);
                setShowCustomDatePicker(false);
              }}
            >
              All Time
            </button>
            <button
              className={`date-btn ${dateRange === 'today' ? 'active' : ''}`}
              onClick={() => {
                setDateRange('today');
                setCurrentPage(1);
                setShowCustomDatePicker(false);
              }}
            >
              Today
            </button>
            <button
              className={`date-btn ${dateRange === 'last-week' ? 'active' : ''}`}
              onClick={() => {
                setDateRange('last-week');
                setCurrentPage(1);
                setShowCustomDatePicker(false);
              }}
            >
              Last Week
            </button>
            <button
              className={`date-btn ${dateRange === 'this-month' ? 'active' : ''}`}
              onClick={() => {
                setDateRange('this-month');
                setCurrentPage(1);
                setShowCustomDatePicker(false);
              }}
            >
              This Month
            </button>
            <button
              className={`date-btn ${dateRange === 'last-month' ? 'active' : ''}`}
              onClick={() => {
                setDateRange('last-month');
                setCurrentPage(1);
                setShowCustomDatePicker(false);
              }}
            >
              Last Month
            </button>
            <button
              className={`date-btn ${dateRange === 'custom' ? 'active' : ''}`}
              onClick={() => {
                setDateRange('custom');
                setShowCustomDatePicker(!showCustomDatePicker);
                setCurrentPage(1);
              }}
            >
              Custom
            </button>
          </div>
        </div>

        {/* Custom Date Range Picker */}
        {showCustomDatePicker && (
          <div className="custom-date-range">
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => {
                setCustomStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="Start Date"
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => {
                setCustomEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="date-input"
              placeholder="End Date"
            />
          </div>
        )}
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : paginatedOrders.length === 0 ? (
        <div className="no-data">
          <p>No orders found. {searchTerm || filterStatus !== 'all' ? 'Try adjusting your filters.' : 'Create your first order!'}</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Product</th>
                  <th>Color</th>
                  <th>Salesperson</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                  <th>Payment Status</th>
                  <th>Customer Received</th>
                  <th>Created Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="po-number">{order.po_number}</td>
                    <td>{order.customer_name}</td>
                    <td className="phone">{formatPhoneNumber(order.customer_phone)}</td>
                    <td>{order.item_name}</td>
                    <td>{order.item_color || '-'}</td>
                    <td className="salesperson">{order.sales_person || '-'}</td>
                    <td className="qty">{order.quantity || 1}</td>
                    <td className="price">${parseFloat(order.price || 0).toFixed(2)}</td>
                    <td className="amount">${(parseFloat(order.price || 0) * parseInt(order.quantity || 1)).toFixed(2)}</td>
                    <td>
                      <span
                        className="status-badge"
                        style={{ backgroundColor: order.payment_status === 'Paid in Full' ? '#228B22' : '#FFA500' }}
                      >
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="received-cell">
                      <label className="received-toggle">
                        <input
                          type="checkbox"
                          checked={order.customer_received || false}
                          onChange={() => handleToggleReceived(order.id, order.customer_received)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </td>
                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => handleViewOrder(order)}
                        className="btn-view"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="orders-mobile-list">
            {paginatedOrders.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-card-header">
                  <span className="order-card-po">{order.po_number}</span>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: order.payment_status === 'Paid in Full' ? '#228B22' : '#FFA500' }}
                  >
                    {order.payment_status}
                  </span>
                </div>
                <div className="order-card-body">
                  <div className="order-card-row">
                    <span className="order-card-label">Customer</span>
                    <div className="order-card-value order-card-customer">
                      <span className="order-card-customer-name">{order.customer_name}</span>
                      <span className="order-card-customer-phone">{formatPhoneNumber(order.customer_phone)}</span>
                    </div>
                  </div>
                  <div className="order-card-row">
                    <span className="order-card-label">Product</span>
                    <div className="order-card-value">
                      <span className="order-card-product">{order.item_name}</span>
                      {order.item_color && <span className="order-card-color"> - {order.item_color}</span>}
                    </div>
                  </div>
                  <div className="order-card-row">
                    <span className="order-card-label">Amount</span>
                    <span className="order-card-value order-card-amount">${parseFloat(order.price).toFixed(2)}</span>
                  </div>
                  <div className="order-card-row">
                    <span className="order-card-label">Salesperson</span>
                    <span className="order-card-value">{order.sales_person || '-'}</span>
                  </div>
                </div>
                <div className="order-card-footer">
                  <span className="order-card-date">{new Date(order.created_at).toLocaleDateString()}</span>
                  <div className="order-card-actions">
                    <label className="received-toggle" title="Customer Received">
                      <input
                        type="checkbox"
                        checked={order.customer_received || false}
                        onChange={() => handleToggleReceived(order.id, order.customer_received)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <button
                      onClick={() => handleViewOrder(order)}
                      className="btn-view"
                    >
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn-pagination"
              >
                ← Previous
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="btn-pagination"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleOrderSuccess}
      />

      {/* Jake Order Modal */}
      <JakeOrderModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        onOrdersCreated={() => {
          fetchOrders();
        }}
      />
        </>
      )}
    </div>
  );
};

export default Dashboard;
